/**
 * Pure layout functions extracted from family.js.
 * No DOM dependencies — fully testable in isolation.
 */

export function isPerson(name) {
  return !name.includes(' + ')
}

export function isUnion(name) {
  return name.includes(' + ')
}

/**
 * Parse the family text format into entries.
 * @returns {Object} map<name, list<data rows>>
 */
export function getEntries(text) {
  const lines = text.split('\n')
  const result = {}
  let i = 0

  let uniqueCounter = 0
  function makeUnique(str) {
    uniqueCounter += 1
    return str + '#' + uniqueCounter
  }
  const correctedLabel = (str) =>
    str === '?' || str === '...' ? makeUnique(str) : str

  function trySkipComment() {
    if (
      i >= lines.length ||
      !(lines[i].startsWith('#') || lines[i].trim() === '')
    )
      return false
    i++
    return true
  }

  while (i < lines.length) {
    if (trySkipComment()) continue
    let key = lines[i]
    let toks = key.split(' + ')
    if (toks.length > 2) {
      throw new Error('Multiple + signs in union: ' + key)
    }
    toks = toks.map((x) => x.trim())
    if (toks.includes('')) throw new Error('Misformatted line ' + i + ': ' + key)
    if (key.includes(',')) throw new Error("Names can't contain commas: " + key)
    if (toks.length === 2) {
      toks = toks.map(correctedLabel)
      key = toks[0] + ' + ' + toks[1]
    } else {
      if (result.hasOwnProperty(key)) throw new Error('Multiple entries for name: ' + key)
    }
    const value = []
    i += 1
    while (i < lines.length && lines[i].startsWith(' ')) {
      if (trySkipComment()) continue
      let trimmedLine = lines[i].trim()
      if (
        trimmedLine.substr(1, 2) !== ': ' ||
        (isPerson(key) && !['n', 'l', 'p'].includes(trimmedLine[0])) ||
        (isUnion(key) && !['n', 'c'].includes(trimmedLine[0]))
      ) {
        throw new Error('Misformatted line under ' + key + ': ' + trimmedLine)
      }
      if (trimmedLine.substr(0, 3) === 'c: ') {
        let children = trimmedLine.substr(3).split(', ').map(correctedLabel)
        trimmedLine = 'c: ' + children.join(', ')
        if (children.includes(toks[0]))
          throw new Error(toks[0] + ' is listed as their own child')
        if (children.includes(toks[1]))
          throw new Error(toks[1] + ' is listed as their own child')
      }
      value.push(trimmedLine)
      i += 1
    }
    result[key] = value
  }
  return result
}

/**
 * Build bipartite graph: map<name, list<adjacent names>>
 */
export function getNeighbours(entries) {
  const result = {}
  for (let name of Object.keys(entries)) result[name] = []

  function addHalfEdge(x, y) {
    if (!result.hasOwnProperty(x)) result[x] = []
    result[x].push(y)
  }

  for (let [name, props] of Object.entries(entries)) {
    if (isPerson(name)) continue
    const [p1, p2] = name.split(' + ')
    const newName = p1 + ' + ' + p2
    let children = []
    for (const prop of props) {
      if (prop.startsWith('c: ')) children = prop.substring(3).split(', ')
    }
    for (const x of children.concat([p1, p2])) {
      addHalfEdge(newName, x)
      addHalfEdge(x, newName)
    }
  }
  return result
}

export function getUnion(person, neighbours, side) {
  const result = []
  for (let name of neighbours[person]) {
    const members = name.split(' + ')
    if (members[1 - side] === person) result.push(name)
  }
  if (result.length === 0) return null
  else if (result.length === 1) return result[0]
  else throw new Error(person + ' has two unions on side ' + side)
}

export function getLeftUnion(person, neighbours) {
  return getUnion(person, neighbours, 0)
}

export function getRightUnion(person, neighbours) {
  return getUnion(person, neighbours, 1)
}

export function getAboveUnion(person, neighbours) {
  for (let name of neighbours[person]) {
    if (!name.split(' + ').includes(person)) return name
  }
  return null
}

export function getChildren(union, neighbours) {
  if (union === null) return []
  return neighbours[union].filter((name) => !union.split(' + ').includes(name))
}

export function getRenderedChildren(union, neighbours, layout) {
  return getChildren(union, neighbours).filter((child) => layout.hasOwnProperty(child))
}

export function shift(layout, delta, sign = 1) {
  const [dx, dy] = [delta.x, delta.y]
  for (const pt of Object.values(layout)) {
    pt.x += sign * dx
    pt.y += sign * dy
  }
}

export function xRadius(name, divs, paddingAmount) {
  if (isUnion(name)) return 0
  return paddingAmount + divs[name].offsetWidth / 2
}

export function rowRanges(layout, divs, paddingAmount) {
  const result = {}
  for (const [name, pt] of Object.entries(layout)) {
    const delta = xRadius(name, divs, paddingAmount)
    const isOld = result.hasOwnProperty(pt.y)
    result[pt.y] = {
      min: Math.min(...[pt.x - delta].concat(isOld ? [result[pt.y].min] : [])),
      max: Math.max(...[pt.x + delta].concat(isOld ? [result[pt.y].max] : [])),
    }
  }
  return result
}

export function collides(left, right, divs, paddingAmount) {
  const layers = {}
  for (const [name, pt] of Object.entries(left).concat(Object.entries(right))) {
    if (!layers.hasOwnProperty(pt.y)) layers[pt.y] = []
    layers[pt.y].push([
      pt.x - xRadius(name, divs, paddingAmount),
      pt.x + xRadius(name, divs, paddingAmount),
    ])
  }
  for (const intervals of Object.values(layers)) {
    const sorted = intervals.sort((a, b) =>
      a[0] === b[0] ? a[1] - b[1] : a[0] - b[0],
    )
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i][1] > sorted[i + 1][0]) return true
    }
  }
  return false
}

export function mergedLayout(left, right, divs, paddingAmount, moveRight = true, tryUnder = false) {
  if (tryUnder && !collides(left, right, divs, paddingAmount)) {
    return Object.assign(left, right)
  }
  const lbounds = rowRanges(left, divs, paddingAmount)
  const rbounds = rowRanges(right, divs, paddingAmount)
  let shiftage = null
  for (const y of Object.keys(lbounds)) {
    if (rbounds.hasOwnProperty(y)) {
      const delta = lbounds[y].max - rbounds[y].min
      shiftage = shiftage === null ? delta : Math.max(shiftage, delta)
    }
  }
  if (shiftage === null) throw new Error('merge without common y')
  if (moveRight) shift(right, { x: shiftage, y: 0 })
  else shift(left, { x: -shiftage, y: 0 })
  return Object.assign(left, right)
}

// Replaces Set.prototype.union monkey-patch
function setUnion(setA, setB) {
  const union = new Set(setA)
  for (const elem of setB) {
    union.add(elem)
  }
  return union
}

export function getVisibleNodes(name, pred, neighbours, includeAll, downLimit, path = null, visited = null) {
  if (path === null) {
    path = { allowUp: true, downsLeft: downLimit, desc: true }
  }
  if (visited === null) {
    visited = new Set()
  }
  if (includeAll) {
    return new Set(Object.keys(neighbours))
  }
  if (visited.has(name)) {
    return new Set([name])
  }
  visited.add(name)
  const getNodes = function (newName, newPath) {
    if (newName === null || newName === pred) return new Set([])
    return getVisibleNodes(
      newName,
      name,
      neighbours,
      includeAll,
      downLimit,
      Object.assign({}, path, newPath),
      visited,
    )
  }
  if (isPerson(name)) {
    const leftUnion = getLeftUnion(name, neighbours)
    const rightUnion = getRightUnion(name, neighbours)
    const aboveUnion = path.allowUp ? getAboveUnion(name, neighbours) : null
    return setUnion(
      setUnion(
        setUnion(new Set([name]), getNodes(aboveUnion, { desc: false })),
        getNodes(leftUnion, { allowUp: false }),
      ),
      getNodes(rightUnion, { allowUp: false }),
    )
  } else {
    const [leftParent, rightParent] = name.split(' + ')
    const children =
      !path.desc && path.downsLeft === 0 ? [] : getChildren(name, neighbours)
    let result = setUnion(
      setUnion(new Set([name]), getNodes(leftParent, {})),
      getNodes(rightParent, {}),
    )
    for (const child of children) {
      result = setUnion(
        result,
        getNodes(child, { allowUp: false, downsLeft: path.downsLeft - 1 }),
      )
    }
    return result
  }
}

export function dumbLayout(name, pred, neighbours, divs, visibleNodes, paddingAmount) {
  const doLayout = function (next, defaultLocation = { x: 0, y: 0 }) {
    if (next === null || !visibleNodes.has(next)) return null
    if (next === pred) return { [name]: { x: 0, y: 0 }, [next]: defaultLocation }
    const result = dumbLayout(next, name, neighbours, divs, visibleNodes, paddingAmount)
    shift(result, result[name], -1)
    return result
  }

  let mainLayout = { [name]: { x: 0, y: 0 } }
  let leftLayout, rightLayout

  if (isPerson(name)) {
    const leftUnion = getLeftUnion(name, neighbours)
    const rightUnion = getRightUnion(name, neighbours)
    const aboveUnion = getAboveUnion(name, neighbours)
    leftLayout = doLayout(leftUnion)
    rightLayout = doLayout(rightUnion)
    const aboveLayout = doLayout(aboveUnion, { x: 0, y: -1 })
    if (aboveLayout !== null) mainLayout = aboveLayout
  } else {
    const [leftParent, rightParent] = name.split(' + ')
    const children = getChildren(name, neighbours).filter((x) => visibleNodes.has(x))
    leftLayout = doLayout(leftParent)
    rightLayout = doLayout(rightParent)
    const childLayouts = children.map((child) => doLayout(child, { x: 0, y: 1 }))
    if (childLayouts.length > 0) {
      for (const childLayout of childLayouts) delete childLayout[name]
      mainLayout = childLayouts[0]
      for (const childLayout of childLayouts.slice(1))
        mainLayout = mergedLayout(mainLayout, childLayout, divs, paddingAmount)
      const childXs = children.map((child) => mainLayout[child].x)
      const middle = (Math.min(...childXs) + Math.max(...childXs)) / 2
      shift(mainLayout, { x: -middle, y: 0 })
      mainLayout[name] = { x: 0, y: 0 }
    }
  }

  if (leftLayout !== null) {
    delete leftLayout[name]
    mainLayout = mergedLayout(leftLayout, mainLayout, divs, paddingAmount, false, isPerson(name))
  }
  if (rightLayout !== null) {
    delete rightLayout[name]
    mainLayout = mergedLayout(mainLayout, rightLayout, divs, paddingAmount, true, isPerson(name))
  }
  return mainLayout
}

export function boundingBox(layout, divs, paddingAmount) {
  function xbound(entry, sign) {
    const [name, pt] = entry
    return pt.x + (isUnion(name) ? 0 : sign * (paddingAmount + divs[name].offsetWidth / 2))
  }
  return {
    bottomLeft: {
      x: Math.min(...Object.entries(layout).map((entry) => xbound(entry, -1))),
      y: Math.min(...Object.values(layout).map((pt) => pt.y)),
    },
    topRight: {
      x: Math.max(...Object.entries(layout).map((entry) => xbound(entry, +1))),
      y: Math.max(...Object.values(layout).map((pt) => pt.y)),
    },
  }
}

export function adjustUnions(neighbours, layout, divs, lineHeight) {
  for (const node of Object.keys(layout)) {
    if (!isUnion(node)) continue
    const children = getRenderedChildren(node, neighbours, layout)
    if (children.length === 0) continue
    const [p1, p2] = node.split(' + ')
    const parentBottom = Math.max(
      layout[p1].y + divs[p1].offsetHeight / 2,
      layout[p2].y + divs[p2].offsetHeight / 2,
    )
    let childTop = layout[children[0]].y - divs[children[0]].offsetHeight / 2
    for (const child of children) {
      childTop = Math.min(childTop, layout[child].y - divs[child].offsetHeight / 2)
    }
    if (childTop < parentBottom) {
      console.warn('Union ' + node + ' overlapped above/below. Try increasing lineHeight')
    }
    layout[node].y = (parentBottom + childTop) / 2
  }
}

export function computeLayout(rootName, neighbours, divs, includeAll, downLimit, paddingAmount, lineHeight) {
  const visibleNodes = getVisibleNodes(rootName, null, neighbours, includeAll, downLimit)
  const layout = dumbLayout(rootName, null, neighbours, divs, visibleNodes, paddingAmount)
  shift(layout, boundingBox(layout, divs, paddingAmount).bottomLeft, -1)
  shift(layout, { x: 0, y: 1 })
  for (const pt of Object.values(layout)) {
    pt.y *= lineHeight
  }
  adjustUnions(neighbours, layout, divs, lineHeight)
  return layout
}

export function validateTreeStructure(neighbours) {
  const parent = {}
  function buildConnectedComponent(curr, prev, component) {
    if (parent.hasOwnProperty(curr)) {
      let loop = [curr, prev]
      let unroll = prev
      while (unroll !== curr) {
        if (unroll === null) {
          throw new Error('Internal validation error')
        }
        unroll = parent[unroll]
        loop.push(unroll)
      }
      throw new Error('Loop detected: ' + loop)
    }
    parent[curr] = prev
    component[curr] = true
    for (const x of neighbours[curr]) {
      if (x === prev) continue
      buildConnectedComponent(x, curr, component)
    }
  }

  const components = []
  for (const name of Object.keys(neighbours)) {
    if (!neighbours.hasOwnProperty(name)) {
      throw new Error('Singleton node or malformatted line: ' + name)
    }
    if (parent.hasOwnProperty(name)) continue
    const component = {}
    buildConnectedComponent(name, null, component)
    components.push([name, component])
  }
  if (components.length > 1) {
    let msg = 'Multiple connected components'
    for (const [name, component] of components) {
      msg += ' | ' + Object.keys(component).length + ' connected to ' + name
    }
    throw new Error(msg)
  }
}

export function displayName(name) {
  return name.replace(/#.*$/g, '')
}
